import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import FormThreadsTree from './FormThreadsTree';
import FormUsersList from '../teams/FormUsersList';
import ThemedSelect from '../../utils/ThemedSelect';
import ConfirmationModal from '../../utils/ConfirmationModal';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../utils/PermissionsContext';

const PROJECT_ROLES = [
  { value: 'PROJECT_MANAGER', label: 'Project Manager' },
  { value: 'DOSSIER_DEV_LEAD', label: 'Dossier Development Lead' },
  { value: 'DOSSIER_VIEWER', label: 'Dossier Viewer' }
];

const MEMBER_ROLE_COL_WIDTH = 'var(--px-210)';

// The only two tag buckets a project's tags may live in. Tags are still free
// text within a bucket (see TagsSelector) — there is no more ungrouped tag.
const CATEGORY_TYPES = [
  { key: 'indication', label: 'Indication', icon: 'bi-clipboard2-pulse', placeholder: 'Search or create indication tag' },
  { key: 'intervention', label: 'Intervention', icon: 'bi-capsule', placeholder: 'Search or create intervention tag' }
];
const EMPTY_CATEGORY = { indication: [], intervention: [] };

// Staged member edits as the backend takes them.
const toMemberChangesPayload = (changes) =>
  Object.entries(changes).map(([user_id, change]) =>
    change.action === 'remove'
      ? { user_id, action: 'remove' }
      : { user_id, action: 'assign', role: change.role }
  );

const sameMemberChanges = (a, b) => {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every(key =>
    b[key] && b[key].action === a[key].action && b[key].role === a[key].role
  );
};

// Multi-select Tags Selector — used once per bucket (Indication, Disease).
// Free text within whichever bucket it's given: search an existing tag in
// that bucket, or type a new one and create it. It has no idea buckets
// exist; the parent just gives it that bucket's own tag list.
const TagsSelector = ({
  categories = [],
  selectedTags = [],
  onSelect,
  placeholder = "Search or create tag"
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [allTags, setAllTags] = useState(categories);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync allTags if categories prop changes
  useEffect(() => {
    setAllTags(prev => {
      const merged = [...new Set([...prev, ...categories])];
      return merged;
    });
  }, [categories]);

  const filteredTags = useMemo(() => {
    if (!searchTerm.trim()) return allTags;
    return allTags.filter(tag =>
      tag.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allTags, searchTerm]);

  // Check if search term is a new tag (doesn't exist in list)
  const isNewTag = useMemo(() => {
    if (!searchTerm.trim()) return false;
    return !allTags.some(tag => tag.toLowerCase() === searchTerm.toLowerCase());
  }, [allTags, searchTerm]);

  // Toggle tag selection
  const handleToggle = (tag) => {
    const newSelected = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    onSelect(newSelected);
  };

  // Create new tag and select it
  const handleCreate = () => {
    const newTag = searchTerm.trim();
    if (!newTag) return;
    if (!allTags.includes(newTag)) {
      setAllTags(prev => [...prev, newTag]);
    }
    if (!selectedTags.includes(newTag)) {
      onSelect([...selectedTags, newTag]);
    }
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isNewTag) handleCreate();
    }
  };

  return (
    <div className="position-relative mt-2" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        className="formField3 ev-fs-12 ev-radius-small cursor-pointer d-flex align-items-center justify-content-between w-100"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>
          <i className="bi bi-tags"></i>&nbsp;
          {selectedTags.length === 0
            ? 'Select tags'
            : `${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''} selected`}
        </span>
        <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </button>


      {/* Dropdown */}
      {isOpen && (
        <div className="ev-message-card2 ev-card-3 position-absolute w-100 z-1000 mt-1">
          {/* Search Input */}
          <div className="mb-2">
            <div className="position-relative">
              {/* search-input's own left padding leaves room for the icon;
                  m-0 drops its 5px margin, shadow-none drops the focus glow. */}
              <input
                type="text"
                className="form-control search-input m-0 shadow-none"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <i className="bi bi-search position-absolute top-50 translate-middle-y start-0 ms-3 ev-small-p-blue"></i>
            </div>
          </div>

          {/* Tags List */}
          <div className="scrollable-element vh-25">
            {/* Create New Tag Button */}
            {isNewTag && (
              <div
                className="mb-1 ev-btn-small ev-radius-xsmall ev-reg-p-blue ev-fs-12 ev-btn-blue-fill-round cursor-pointer ev-padding-small"
                onClick={handleCreate}
              >
                <i className="bi bi-plus-circle"></i>&nbsp;
                Create "{searchTerm}"
              </div>
            )}

            {/* Existing Tags */}
            {filteredTags.length > 0 ? (
              filteredTags.map((tag, index) => (
                <div
                  key={index}
                  className={`mb-1 ev-btn-small ev-radius-xsmall cursor-pointer ev-bold-med ev-xsmall-p-blue ev-padding-small d-flex align-items-center ${
                    selectedTags.includes(tag) ? 'ev-btn-lightblue-fill-round' : ''
                  }`}
                  onClick={() => handleToggle(tag)}
                >
                  <i className={`bi ${selectedTags.includes(tag) ? 'bi-check-square-fill' : 'bi-tag'} me-1`}></i>
                  {tag}
                </div>
              ))
            ) : (
              !isNewTag && (
                <div className="ev-padding-small text-muted">No tags found</div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DataFabricForm = ({
  editingFabric,
  onSuccess,
  onCancel,
  f_cats,
  fromProject = false,
  onEditFolderFromProject,
  onCreateFolderFromProject
}) => {
  // Pending navigation away from this form (folder edit/create).
  // Holds the action to run once the user confirms losing unsaved changes.
  const [pendingLeaveAction, setPendingLeaveAction] = useState(null);

  const requestLeave = (action) => setPendingLeaveAction(() => action);

  const cancelLeave = () => setPendingLeaveAction(null);

  const confirmLeave = () => {
    const action = pendingLeaveAction;
    setPendingLeaveAction(null);
    if (action) action();
  };

  // Form state. category is { indication: [...], disease: [...] } — tags are
  // still free text, just filed under one of these two buckets.
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fabric_access_level: 'PRIVATE',
    threads: [],
    category: { ...EMPTY_CATEGORY }
  });
  
  // Country and project roles
  const [creatableCountries, setCreatableCountries] = useState([]);
  const [countryId, setCountryId] = useState('');
  const [memberRoles, setMemberRoles] = useState({});
  // Member edits on an existing project, staged until Update Project:
  const [memberChanges, setMemberChanges] = useState({});
  const memberChangesRef = useRef({});
  const lastValidMemberChangesRef = useRef({});
  const validationSeqRef = useRef(0);
  const [isValidatingMembers, setIsValidatingMembers] = useState(false);

  // Set when the backend says this user may no longer edit the project (their
  // role changed while the form was open). The form and every staged change
  // stay as they are — only a note is shown, with the popup once — so nothing
  // the user typed is lost; it clears on the next save/validate that works.
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');
  // A ref, not the state above: validations resolve asynchronously and would
  // otherwise read a stale value and raise the popup more than once.
  const accessDeniedShownRef = useRef(false);
  const { refreshPermissions } = usePermissions();
  const [pendingDiscardAction, setPendingDiscardAction] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [grantableRoles, setGrantableRoles] = useState([]);

  const [handoverMember, setHandoverMember] = useState(null);
  const [handoverNewPm, setHandoverNewPm] = useState(null);
  // Identity-seed race guard. seedCreatorAsPM (below) is an async call that
  // can resolve after the form is already interactive, so it needs to know
  // whether to still assert Project Manager once it lands, or stand down
  // because the user already made a deliberate choice about their own row
  // (changed their role, or removed themselves).
  //
  // Tracked by user_id, not by a single "was the creator touched" boolean:
  // the creator's own id is only known once /authorizationcheck responds, so
  // a fast click on their own row before that lands would otherwise register
  // on memberRoles without being marked as touched — and the seed effect
  // would then silently re-add Project Manager after the user had just
  // removed themselves.
  const touchedUserIdsRef = useRef(new Set());
  // The current user's own id, once /authorizationcheck resolves. Lets the
  // "you're added as..." banner below read back whatever role is actually
  // sitting in memberRoles for this user, instead of a hardcoded label.
  const [currentUserId, setCurrentUserId] = useState(null);
  // Country as the details endpoint reports it.
  const [detailsCountryId, setDetailsCountryId] = useState(null);

  // A project only runs on project roles once it has a country.
  const isConverted = Boolean(detailsCountryId ?? (editingFabric && editingFabric.country_id));

  // What this user may actually do on this project. The backend answers this
  // with the same checks the write endpoints use — the rules are not repeated
  // here, because "Admin" and "Global Viewer" look identical from the list row
  // (both have my_project_role null) yet only one of them can edit.
  // Creating is a different question, gated by the countries list instead.
  const [canEdit, setCanEdit] = useState(!editingFabric);
  const [permsLoaded, setPermsLoaded] = useState(!editingFabric);
  const readOnly = Boolean(editingFabric) && !canEdit;

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const navigate = useNavigate();

  // API base URL
  const API_BASE_URL = '/pceapi/fabrics';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Load initial data if editing
  useEffect(() => () => { validationSeqRef.current += 1; }, []);

  useEffect(() => {
    if (editingFabric) {
      resetMemberChanges();
      // editingFabric.category now comes back as { indication: [...], intervention: [...] }.
      // Defensive against older cached data still shaped as a flat array.
      const incomingCategory = editingFabric.category;
      const category = incomingCategory && typeof incomingCategory === 'object' && !Array.isArray(incomingCategory)
        ? {
            indication: Array.isArray(incomingCategory.indication) ? incomingCategory.indication : [],
            intervention: Array.isArray(incomingCategory.intervention) ? incomingCategory.intervention : []
          }
        : { ...EMPTY_CATEGORY };

      setFormData({
        name: editingFabric.name || '',
        description: editingFabric.description || '',
        fabric_access_level: editingFabric.fabric_access_level || 'PRIVATE',
        threads: [],
        category
      });
      
      // Fetch threads for the fabric we're editing to get the IDs
      if (editingFabric.id) {
        fetchFabricThreads(editingFabric.id);
        // One call for permissions, country and members together.
        fetchFabricDetails(editingFabric.id);
      }
    }
  }, [editingFabric]);

  // Countries this user may create a project in. An empty list means they cannot
  // create one at all — the same rule the backend enforces on submit.
  useEffect(() => {
    fetchCreatableCountries();
  }, []);

  // Default the creator to Project Manager on a fresh project. 
  useEffect(() => {
    if (editingFabric) return;
    const seedCreatorAsPM = async () => {
      try {
        const response = await axios.post(
          '/pceapi/auth/iamuser/authorizationcheck',
          {},
          { headers }
        );
        const data = response.data;
        if (data.status === 'Authorized' && data.user_id) {
          // Remembered so the "you're added as..." banner can reflect
          // whatever role ends up in memberRoles for this user.
          setCurrentUserId(data.user_id);
          // Project Manager is the default for the creator's own row, but it
          // never overrides a role the user picked for themselves on purpose.
          if (!touchedUserIdsRef.current.has(data.user_id)) {
            setMemberRoles(prev => ({
              ...prev,
              [data.user_id]: { email: data.username, role: 'PROJECT_MANAGER' }
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    seedCreatorAsPM();
  }, [editingFabric]);

  const fetchCreatableCountries = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/creatable-countries`, { headers });
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      if (data.status === 'success') {
        setCreatableCountries(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

    // Single source for everything the form needs about an existing project:
  // whether this user may edit it, whether it has a country yet, and who holds
  // which project role. Fails closed on error — a read-only banner is a safer
  // wrong answer than an editable form whose save gets rejected.
  const fetchFabricDetails = async (fabricId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/details/${fabricId}`, { headers });
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      const fabric = data.status === 'success' && data.data ? data.data.fabric : null;
      if (fabric) {
        setCanEdit(Boolean(fabric.can_edit));
        setDetailsCountryId(fabric.country_id ?? null);
        setProjectMembers(data.data.project_members || []);
        setGrantableRoles(fabric.grantable_project_roles || []);
      } else {
        setCanEdit(false);
        setGrantableRoles([]);
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
      setCanEdit(false);
      setGrantableRoles([]);
    } finally {
      setPermsLoaded(true);
    }
  };

  // Per-bucket tag select handler — updates just the one bucket (indication or
  // disease) the tag was picked under.
  const handleSelectTags = (categoryType, tags) => {
    setFormData(prev => ({
      ...prev,
      category: { ...prev.category, [categoryType]: tags }
    }));
  };

  const fetchFabricThreads = async (fabricId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/threads-tree`,
        {
          page: 1,
          size: 100, // Get a large number to ensure we get all threads
          fabric_id: fabricId
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'success' && data.data) {
        // Extract thread IDs that are in this fabric
        const fabricThreadIds = data.data
          .filter(thread => thread.is_in_fabric)
          .map(thread => thread.id);
        
        setFormData(prev => ({
          ...prev,
          threads: fabricThreadIds
        }));
      }
    } catch (error) {
      console.error('Error fetching project collections:', error);
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle thread selection (multi-select for fabrics)
  const handleThreadSelection = (threadId) => {
    setFormData(prev => {
      const threads = [...prev.threads];
      if (threads.includes(threadId)) {
        return { ...prev, threads: threads.filter(id => id !== threadId) };
      } else {
        return { ...prev, threads: [...threads, threadId] };
      }
    });
  };

  // Shared response handling for the immediate member and country actions.
  // Returns true on success; on failure the popup has already been shown.
  const postAction = async (path, body, processingLabel, fallbackError) => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage(processingLabel);
    try {
      const response = await axios.post(`${API_BASE_URL}${path}`, body, { headers });
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return false;
      }
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || fallbackError);
        setIsErrorPopupVisible(true);
        return false;
      }
      setIsProcessingPopupVisible(false);
      return true;
    } catch (error) {
      console.error(`${path} error:`, error);
      setIsProcessingPopupVisible(false);
      setPopupErrorMessage(error.message || fallbackError);
      setIsErrorPopupVisible(true);
      return false;
    }
  };

  // Picker toggle. A newly picked user starts on the least-privileged role.
  const handleMemberToggle = (userId, user) => {
    touchedUserIdsRef.current.add(userId);
    setMemberRoles(prev => {
      if (prev[userId]) {
        const next = { ...prev };
        delete next[userId];
        return next;
      }
      return {
        ...prev,
        [userId]: { email: user ? user.email : userId, role: 'DOSSIER_VIEWER' }
      };
    });
  };

  const handleMemberRoleChange = (userId, role) => {
    touchedUserIdsRef.current.add(userId);
    setMemberRoles(prev => ({ ...prev, [userId]: { ...prev[userId], role } }));
  };

  const membersPayload = () =>
    Object.entries(memberRoles).map(([user_id, value]) => ({ user_id, role: value.role }));

  // ---- Members of an existing project: staged, validated per edit ----------

  const resetMemberChanges = () => {
    const empty = {};
    validationSeqRef.current += 1;
    memberChangesRef.current = empty;
    lastValidMemberChangesRef.current = empty;
    setMemberChanges(empty);
    setIsValidatingMembers(false);
  };

  // Asks the backend whether the staged set would be accepted.
  const validateMemberChanges = async (payload) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/members/validate`,
        { fabric_id: editingFabric.id, member_changes: payload },
        { headers }
      );
      const data = response.data;
      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return { ok: false, rejected: false };
      }
      if (data.status === 'failed') {
        return { ok: false, rejected: true, message: data.message, userId: data.user_id, code: data.code };
      }
      return { ok: true };
    } catch (error) {
      console.error('Error validating member changes:', error);
      return { ok: false, rejected: false };
    }
  };

  const memberErrorMessage = (changes, message, userId) => {
    const email = userId && changes[userId] ? changes[userId].email : null;
    const text = message || 'This member change is not allowed.';
    return email ? `${email}: ${text}` : text;
  };

  // Popup only the first time; after that the note by the Save button is
  // enough, so editing on doesn't raise the same popup again and again.
  const handleAccessDenied = (message) => {
    const text = message || "Access denied. Your role in this project doesn't allow this action.";
    if (!accessDeniedShownRef.current) {
      accessDeniedShownRef.current = true;
      setPopupErrorMessage(text);
      setIsErrorPopupVisible(true);
    }
    setAccessDeniedMessage(text);
    refreshPermissions();
  };

  const stageMemberChanges = (next, prev) => {
    memberChangesRef.current = next;
    setMemberChanges(next);

    const seq = ++validationSeqRef.current;
    const payload = toMemberChangesPayload(next);
    if (payload.length === 0) {
      lastValidMemberChangesRef.current = next;
      setIsValidatingMembers(false);
      return;
    }

    setIsValidatingMembers(true);
    validateMemberChanges(payload).then(result => {
      if (seq !== validationSeqRef.current) return;
      setIsValidatingMembers(false);
      if (result.ok) {
        lastValidMemberChangesRef.current = next;
        accessDeniedShownRef.current = false;
        setAccessDeniedMessage('');
        return;
      }
      if (!result.rejected) return;

      // Not a problem with this change — the user just can't edit any more.
      // Keep it (and everything staged before it) instead of undoing it.
      if (result.code === 'ACCESS_DENIED') {
        handleAccessDenied(result.message);
        return;
      }

      const fallback = sameMemberChanges(prev, lastValidMemberChangesRef.current)
        ? prev
        : lastValidMemberChangesRef.current;
      validationSeqRef.current += 1;
      memberChangesRef.current = fallback;
      setMemberChanges(fallback);
      setPopupErrorMessage(memberErrorMessage(next, result.message, result.userId));
      setIsErrorPopupVisible(true);
    });
  };

  const setStagedMemberChange = (userId, change) => {
    const prev = memberChangesRef.current;
    const next = { ...prev };
    const saved = projectMembers.find(m => m.user_id === userId);
    if (!change || (change.action === 'assign' && saved && saved.role === change.role)) {
      delete next[userId];
    } else {
      next[userId] = change;
    }
    if (sameMemberChanges(prev, next)) return;
    stageMemberChanges(next, prev);
  };

  // Staged additions come first, newest on top, so a member just ticked in the
  // picker shows up at the top of the (scrollable) list instead of below it.
  const memberRows = useMemo(() => {
    const savedIds = new Set(projectMembers.map(m => m.user_id));
    const newRows = [];
    const savedRows = projectMembers.map(member => {
      const change = memberChanges[member.user_id];
      return {
        user_id: member.user_id,
        email: member.email,
        savedRole: member.role,
        role: change && change.action === 'assign' ? change.role : member.role,
        isNew: false,
        isRemoved: Boolean(change && change.action === 'remove')
      };
    });
    Object.entries(memberChanges).forEach(([userId, change]) => {
      if (!savedIds.has(userId) && change.action === 'assign') {
        newRows.unshift({
          user_id: userId,
          email: change.email,
          savedRole: null,
          role: change.role,
          isNew: true,
          isRemoved: false
        });
      }
    });
    return [...newRows, ...savedRows];
  }, [projectMembers, memberChanges]);

  // The list scrolls once it grows, so bring the top back into view whenever a
  // new member is added there.
  const memberListRef = useRef(null);
  const newestMemberId = memberRows.length > 0 && memberRows[0].isNew ? memberRows[0].user_id : null;
  useEffect(() => {
    if (newestMemberId && memberListRef.current) {
      memberListRef.current.scrollTop = 0;
    }
  }, [newestMemberId]);

  const handleStageMemberToggle = (userId, user) => {
    const change = memberChangesRef.current[userId];
    const isSaved = projectMembers.some(m => m.user_id === userId);
    if (change && (change.action === 'remove' || !isSaved)) {
      setStagedMemberChange(userId, null);
      return;
    }
    if (isSaved) return;
    setStagedMemberChange(userId, {
      action: 'assign',
      role: 'DOSSIER_VIEWER',
      email: user ? user.email : userId
    });
  };

  const handleChangeMemberRole = (row, role) => {
    setStagedMemberChange(row.user_id, { action: 'assign', role, email: row.email });
  };

  const memberChangeCount = Object.keys(memberChanges).length;

  const runWithRoleChangeGuard = (action) => {
    if (memberChangeCount > 0) {
      setPendingDiscardAction(() => action);
      return;
    }
    action();
  };

  const cancelDiscard = () => setPendingDiscardAction(null);

  const confirmDiscard = () => {
    const action = pendingDiscardAction;
    setPendingDiscardAction(null);
    resetMemberChanges();
    if (action) action();
  };

  const canManageMember = (row) => row.isNew || grantableRoles.includes(row.savedRole);

  const roleOptionsFor = (row) =>
    PROJECT_ROLES.filter(
      role => grantableRoles.includes(role.value)
        || role.value === row.savedRole
        || role.value === row.role
    );

  const isLastProjectManager = (member) =>
    member.role === 'PROJECT_MANAGER' &&
    projectMembers.filter(m => m.role === 'PROJECT_MANAGER').length === 1;

  const needsHandover = (row) => {
    if (row.isNew) return false;
    const saved = projectMembers.find(m => m.user_id === row.user_id);
    if (!saved || !isLastProjectManager(saved)) return false;
    return !memberRows.some(r =>
      r.user_id !== row.user_id && !r.isRemoved && r.role === 'PROJECT_MANAGER'
    );
  };

  const handleRemoveMember = (row) => {
    // The button is disabled in this case; this also stops the handover dialog
    // from opening off a members list that went stale since it was fetched.
    if (!canManageMember(row)) return;
    if (row.isNew) {
      setStagedMemberChange(row.user_id, null);
      return;
    }
    if (needsHandover(row)) {
      setHandoverNewPm(null);
      setHandoverMember(projectMembers.find(m => m.user_id === row.user_id));
      return;
    }
    setStagedMemberChange(row.user_id, { action: 'remove', email: row.email });
  };

  const handleUndoRemoveMember = (row) => setStagedMemberChange(row.user_id, null);

  const handleHandoverSubmit = async () => {
    if (!handoverMember || !handoverNewPm) return;
    const ok = await postAction(
      '/members/remove',
      {
        fabric_id: editingFabric.id,
        user_id: handoverMember.user_id,
        new_pm_user_id: handoverNewPm.user_id
      },
      'Handing over the project',
      'Failed to hand over the project'
    );
    if (ok) {
      const pruned = { ...memberChangesRef.current };
      delete pruned[handoverMember.user_id];
      delete pruned[handoverNewPm.user_id];
      lastValidMemberChangesRef.current = {};
      stageMemberChanges(pruned, {});
      setHandoverMember(null);
      setHandoverNewPm(null);
      fetchFabricDetails(editingFabric.id);
    }
  };

  // Country is write-once, so this is its own action rather than part of Update —
  // a routine rename should never be able to convert a project by accident.
  const handleAssignCountry = async () => {
    if (!countryId) {
      setPopupErrorMessage('Select a country to assign');
      setIsErrorPopupVisible(true);
      return;
    }
    const members = membersPayload();
    const ok = await postAction(
      '/assign-country',
      {
        fabric_id: editingFabric.id,
        country_id: Number(countryId),
        initial_members: members.length > 0 ? members : null
      },
      'Assigning country',
      'Failed to assign country'
    );
    if (ok) {
      onSuccess('Country assigned successfully');
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Belt and braces: the controls are disabled below, but a read-only user
    // should never reach the endpoint even via a stray Enter keypress.
    if (readOnly) {
      setPopupErrorMessage("You have view-only access to this project.");
      setIsErrorPopupVisible(true);
      return;
    }
    
    // Validation
    if (!formData.name.trim()) {
      setPopupErrorMessage('Project name is required');
      setIsErrorPopupVisible(true);
      return;
    }

    // Every new project gets a country, so it runs on role-based access from the
    // start. Existing projects without one are converted through Assign Country.
    if (!editingFabric && !countryId) {
      setPopupErrorMessage('Please select a country for this project');
      setIsErrorPopupVisible(true);
      return;
    }

    saveFabric();
  };

  const saveFabric = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage(editingFabric ? 'Updating project' : 'Creating project');
    
    try {
      let response;
      
      if (editingFabric) {
        const memberChangesPayload = isConverted
          ? toMemberChangesPayload(memberChangesRef.current)
          : [];
        response = await axios.post(
          `${API_BASE_URL}/update`,
          {
            fabric_id: editingFabric.id,
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            category: formData.category,
            fabric_access_level: formData.fabric_access_level,
            threads: formData.threads.length > 0 ? formData.threads : [],
            ...(memberChangesPayload.length > 0 && { member_changes: memberChangesPayload })
          },
          { headers }
        );
      } else {
        // Create new fabric
        response = await axios.post(
          `${API_BASE_URL}/create`,
          {
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            category: formData.category,
            fabric_access_level: formData.fabric_access_level,
            threads: formData.threads.length > 0 ? formData.threads : [],
            country_id: Number(countryId),
            initial_members: membersPayload()
          },
          { headers }
        );
      }

      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        if (data.code === 'ACCESS_DENIED') {
          handleAccessDenied(data.message);
          return;
        }
        const fallback = editingFabric ? 'Failed to update project' : 'Failed to create project';
        setPopupErrorMessage(
          data.user_id
            ? memberErrorMessage(memberChangesRef.current, data.message || fallback, data.user_id)
            : (data.message || fallback)
        );
        setIsErrorPopupVisible(true);
        return;
      }

      setIsProcessingPopupVisible(false);
      if (editingFabric) {
        resetMemberChanges();
      }
      window.dispatchEvent(new Event('category_updated'));
      onSuccess(data.message || (editingFabric ? 'Project updated successfully' : 'Project created successfully'));
    } catch (error) {
      console.error('Submit error:', error);
      setIsProcessingPopupVisible(false);
      setPopupErrorMessage(error.message || (editingFabric ? 'Failed to update project' : 'Failed to create project'));
      setIsErrorPopupVisible(true);
    }
  };

  const renderMemberPicker = ({ immediate = false } = {}) => (
    <>
      <FormUsersList
        apiBaseUrl="/pceapi/teams"
        selectedUsers={immediate
          ? memberRows.filter(r => !r.isRemoved).map(r => r.user_id)
          : Object.keys(memberRoles)}
        onSelectUser={immediate ? handleStageMemberToggle : handleMemberToggle}
        existingMemberIds={projectMembers
          .filter(m => !(memberChanges[m.user_id] && memberChanges[m.user_id].action === 'remove'))
          .map(m => m.user_id)}
        countryId={isConverted ? (detailsCountryId ?? editingFabric?.country_id) : (countryId || null)}
        countryName={isConverted
          ? (editingFabric?.country_name || null)
          : (creatableCountries.find(c => String(c.id) === String(countryId))?.name || null)}
      />
      {!immediate && Object.keys(memberRoles).length > 0 && (
        <div className="ev-card-3 mt-2">
          {Object.entries(memberRoles).map(([userId, value]) => (
            <div key={userId} className="d-flex align-items-center gap-2 mb-2">
              {/* Tooltip sits on the wrapper, not on the truncating span:
                  .text-truncate sets overflow:hidden, which would clip the
                  tooltip's ::before if both were on the same element. */}
              <div
                className="flex-grow-1 ev-min-w-0 avatar-tooltip"
                data-tooltip={value.email}
              >
                <span className="ev-small-p-dark d-block text-truncate">{value.email}</span>
              </div>
              <div className="flex-shrink-0 themed-select--scaled" style={{ width: MEMBER_ROLE_COL_WIDTH }}>
                <ThemedSelect
                  value={value.role}
                  options={PROJECT_ROLES}
                  onChange={(role) => handleMemberRoleChange(userId, role)}
                  ariaLabel={`Project role for ${value.email}`}
                />
              </div>
              <button
                type="button"
                className="btn ev-btn-red-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 avatar-tooltip flex-shrink-0"
                onClick={() => handleMemberToggle(userId)}
                data-tooltip="Remove from selection"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );

  // Banner under the new-project member picker about the creator's own place
  // on the project. States, in order:
  //   1. identity not known yet            -> default "added as Project Manager"
  //   2. still a member                    -> shows the role actually selected
  //   3. removed, nobody else added        -> "you've removed yourself"
  //   4. removed, someone else is a PM     -> who will manage the project
  //   5. removed, nobody left is a PM      -> warning: no one can manage it
  const renderSelfMembershipNotice = () => {
    if (!currentUserId) {
      return (
        <div className="alert alert-info mt-2">
          <i className="bi bi-info-circle me-1"></i>
          You're added as Project Manager by default. Change your role or
          remove yourself here if that's not what you want.
        </div>
      );
    }

    const self = memberRoles[currentUserId];
    if (self) {
      return (
        <div className="alert alert-info mt-2">
          <i className="bi bi-info-circle me-1"></i>
          You're added as{' '}
          <b>
            {PROJECT_ROLES.find(r => r.value === self.role)?.label || self.role}
          </b>{' '}
          on this project. Change your role or remove yourself here if
          that's not what you want.
        </div>
      );
    }

    const others = Object.entries(memberRoles).filter(([id]) => id !== currentUserId);
    if (others.length === 0) {
      return (
        <div className="alert alert-info mt-2">
          <i className="bi bi-info-circle me-1"></i>
          You've removed yourself from this project's members. Add
          yourself back above if you'd like to remain one.
        </div>
      );
    }

    const managers = others.filter(([, m]) => m.role === 'PROJECT_MANAGER');
    if (managers.length > 0) {
      return (
        <div className="alert alert-info mt-2">
          <i className="bi bi-info-circle me-1"></i>
          You won't be a member of this project.{' '}
          {managers.length === 1
            ? <b>{managers[0][1].email}</b>
            : <b>{managers.length} members</b>}{' '}
          will manage it as Project Manager. Add yourself back above if you
          still need access.
        </div>
      );
    }

    return (
      <div className="ev-alert-card-warning mt-2">
        <i className="bi bi-exclamation-triangle me-1"></i>
        You won't be a member of this project, and no one you've added is a
        Project Manager, so no one will be able to manage it. Make one of them
        a Project Manager, or add yourself back above.
      </div>
    );
  };

  // Popup close handler
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  // f_cats is now { indication: [...], disease: [...] } (was a flat array).
  // Defensive default in case it hasn't loaded yet or an older shape lingers.
  const safeCats = f_cats && !Array.isArray(f_cats) ? f_cats : EMPTY_CATEGORY;

  return (
    <div className="ev-card-2 w-100 ev-padding-xlarge" >
      <p className="ev-med-p-dark mb-4">
        {editingFabric ? (readOnly ? 'Project Details' : 'Edit Project Details') : 'Create Project'}
      </p>

      {readOnly && permsLoaded && (
        <div className="alert alert-info py-2 mb-3" role="status">
          <i className="bi bi-eye me-2"></i>
          <strong>View only.</strong> You can see this project but not change it.
          {isConverted
            ? ' Editing needs a project role here, or Country Head of this project\u2019s country.'
            : ' Editing is limited to the project owner.'}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <fieldset disabled={readOnly} style={readOnly ? { opacity: 0.85 } : undefined}>
        <div className="row">
          <div className="col-md-6">
            <div className="row">
              <div className="col-6">
                <div className="form-group mb-2">
                  <label className="ev-reg-p-dark mb-1">Project Name <span className="text-danger">*</span></label>
                  <input 
                    type="text" 
                    className="formField3" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    required 
                    disabled={fromProject}
                    title={fromProject ? "Project name can't be changed from here" : undefined}
                  />
                </div> 
                {/* <div className="form-group mb-3">
                  <label className="ev-reg-p-dark mb-1">Access Level <span className="text-danger">*</span></label>
                  <div className="btn-group custom-btn-group-outer w-100" role="group" aria-label="Access Level Selection">
                    <button 
                      type="button" 
                      className={`btn custom-btn-group-inner ${formData.fabric_access_level === 'PUBLIC' ?  'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                      onClick={() => setFormData(prev => ({ ...prev, fabric_access_level: 'PUBLIC' }))}
                    >
                      Public
                    </button>
                    <button 
                      type="button" 
                      className={`btn custom-btn-group-inner ${formData.fabric_access_level === 'PRIVATE' ?  'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                      onClick={() => setFormData(prev => ({ ...prev, fabric_access_level: 'PRIVATE' }))}
                    >
                      Private
                    </button>
                  </div>
                </div> */}
              </div>
              <div className="col-6">
                <div className="form-group mb-3">
                  <label className="ev-reg-p-dark mb-1">Description <span className="ev-small-p-blue"><i>(Optional)</i></span></label>
                  <textarea 
                    className="formField3" 
                    name="description" 
                    value={formData.description} 
                    onChange={handleInputChange} 
                    rows="1"
                  />
                </div>
              </div>
            </div>
            
            {/* Country. Write-once: set at creation, or later through Assign
                Country on an existing project. */}
            <div className="form-group mb-3">
              <label className="ev-reg-p-dark mb-1">
                Country {!editingFabric && <span className="text-danger">*</span>}
              </label>
              {isConverted ? (
                <div>
                  <span className="ev-message-card1">{editingFabric.country_name}</span>
                </div>
              ) : editingFabric ? (
                <p className="ev-small-p-dark mb-0">
                  Not assigned yet — use Assign Country on the right.
                </p>
              ) : (
                <ThemedSelect
                  value={countryId}
                  options={creatableCountries.map(c => ({ value: String(c.id), label: c.name }))}
                  onChange={(id) => setCountryId(id)}
                  placeholder="Select country"
                  ariaLabel="Country"
                />
              )}
            </div>

            {/* Tags — one selector per bucket. Still free text within a bucket;
                there is no more "unbucketed" tag. */}
            <div className="mt-2">
              <label className="ev-reg-p-dark mb-1">
                <i className="bi bi-tags"></i> Tags
              </label>
              {CATEGORY_TYPES.map(({ key, label, icon, placeholder }) => (
                // Each bucket sits on its own tinted card, so Indication and
                // Disease read as two separate groups instead of one block.
                <div className="ev-card-1 mb-2" key={key}>
                  <label className="ev-xsmall-p-dark ev-bold-med mb-1 d-block">
                    <i className={`bi ${icon} me-1`}></i>{label}
                  </label>
                  <TagsSelector
                    categories={safeCats[key] || []}
                    selectedTags={formData.category[key] || []}
                    onSelect={(tags) => handleSelectTags(key, tags)}
                    placeholder={placeholder}
                  />
                </div>
              ))}
            </div>

            <div className="form-group mt-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="ev-reg-p-dark mb-0">
                  Data folders Selection 
                  <span className="badge bg-warning  ev-btn-small ms-2">
                    {formData.threads.length} selected
                  </span>
                </label>

                {/* Only when the user arrived via "Manage Data" from a project.
                    The fieldset already disables it in read-only, but a control a
                    viewer can never use is not shown at all. */}
                {fromProject && !readOnly && (
                  <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg text-nowrap"
                    onClick={() => requestLeave(() => onCreateFolderFromProject && onCreateFolderFromProject())}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Add New Data Folder
                  </button>
                )}
              </div>
              
              <FormThreadsTree 
                onSelectThread={handleThreadSelection}
                selectedThreadIds={formData.threads} 
                fabricId={null} 
                multiSelect={true}
                selection={true}
                apiBaseUrl={API_BASE_URL}
                showFolderActions={fromProject && !readOnly}
                onEditFolder={(threadId) =>
                  requestLeave(() => onEditFolderFromProject && onEditFolderFromProject(threadId))
                }
              />
            </div>
          </div>
          
          <div className="col-md-6">
            {/* Everything about who is on the project sits in this column: the
                picker on top and, on an existing project, the members list it
                feeds right under it. */}
            {isConverted ? (
              <>
                <div className="form-group mb-3">
                  <label className="ev-reg-p-dark mb-2 d-block">
                    Add Members
                  </label>
                  {renderMemberPicker({ immediate: true })}
                </div>
                {/* Project roles. Unlike the team above, these rows are what actually
                    grant access to the project. */}
                <div className="form-group mb-3">
                  <label className="ev-reg-p-dark mb-2">
                    Project Members
                    <span className="badge bg-warning ev-btn-small ms-2">
                      {memberRows.filter(r => !r.isRemoved).length}
                    </span>
                    {memberChangeCount > 0 && (
                      <span className="ev-small-p-blue ms-2">
                        {isValidatingMembers ? (
                          <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Checking changes…</>
                        ) : (
                          <><i className="bi bi-info-circle me-1"></i>
                            {memberChangeCount} unsaved change{memberChangeCount > 1 ? 's' : ''} — saved with Update Project</>
                        )}
                      </span>
                    )}
                  </label>
                  {/* The card's top padding moves inside the scroll area (pt-0 here,
                      paddingTop below): row tooltips open upwards, and an
                      overflow:auto box would otherwise clip the first row's.
                      overflowX hidden keeps a wide tooltip from ever adding a
                      horizontal scrollbar. */}
                  <div className="ev-card-3 pt-0">
                    <div
                      ref={memberListRef}
                      className="scrollable-element"
                      style={{
                        height: 'auto',
                        maxHeight: 'var(--vh-40)',
                        overflowX: 'hidden',
                        paddingTop: 'var(--px-15)',
                        // Keeps the scrollbar clear of the row buttons.
                        paddingRight: 'var(--px-8)'
                      }}
                    >
                    {memberRows.length === 0 ? (
                      <p className="text-muted ev-small-p-dark mb-0">No members yet</p>
                    ) : (
                      memberRows.map(row => {
                        const manageable = canManageMember(row);
                        const savedRoleLabel = PROJECT_ROLES.find(r => r.value === row.savedRole)?.label || row.savedRole;
                        // "Removing…" until the backend has accepted this removal,
                        // "Removed" once it has. Read at render: the ref is set in
                        // the same tick the validating flag is cleared.
                        const removalChecked = row.isRemoved && (
                          !isValidatingMembers
                          || (lastValidMemberChangesRef.current[row.user_id]
                            && lastValidMemberChangesRef.current[row.user_id].action === 'remove')
                        );
                        return (
                        <div
                          key={row.user_id}
                          className="d-flex align-items-center gap-2 mb-2"
                        >
                          <div
                            className="flex-grow-1 ev-min-w-0 avatar-tooltip avatar-tooltip-start d-flex align-items-center gap-1"
                            data-tooltip={row.email}
                            style={row.isRemoved ? { opacity: 0.7 } : undefined}
                          >
                            <span
                              className={`ev-small-p-dark text-truncate ev-min-w-0 ${row.isRemoved ? 'text-decoration-line-through' : ''}`}
                            >
                              {row.email}
                            </span>
                            {row.isNew && <span className="badge badge-xsm bg-success flex-shrink-0">New</span>}
                            {row.isRemoved && (
                              <span className="badge badge-xsm bg-danger flex-shrink-0">
                                {removalChecked ? 'Removed' : 'Removing…'}
                              </span>
                            )}
                          </div>
                          <div className="flex-shrink-0 themed-select--scaled" style={{ width: MEMBER_ROLE_COL_WIDTH }}>
                            <ThemedSelect
                              value={row.role}
                              options={roleOptionsFor(row)}
                              onChange={(role) => handleChangeMemberRole(row, role)}
                              disabled={!manageable || row.isRemoved}
                              ariaLabel={`Project role for ${row.email}`}
                            />
                          </div>
                          {row.isRemoved ? (
                            <button
                              type="button"
                              className="btn ev-btn-blue-outline-round ev-radius-xsmall ev-btn-med pe-2 ps-2 avatar-tooltip avatar-tooltip-end flex-shrink-0"
                              onClick={() => handleUndoRemoveMember(row)}
                              data-tooltip="Keep this member"
                            >
                              <i className="bi bi-arrow-counterclockwise"></i>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn ev-btn-red-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 avatar-tooltip avatar-tooltip-end flex-shrink-0"
                              onClick={() => handleRemoveMember(row)}
                              disabled={!manageable}
                              data-tooltip={!manageable
                                ? `Only a Project Manager, the country's head or an Admin can remove a ${savedRoleLabel}`
                                : row.isNew
                                  ? 'Remove from selection'
                                  : needsHandover(row)
                                    ? 'Hand over and remove'
                                    : 'Remove member'}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                        );
                      })
                    )}
                    </div>
                  </div>
                </div>
              </>
            ) : editingFabric ? (
              <div className="form-group mb-3">
                <label className="ev-reg-p-dark mb-2">Assign Country</label>
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-1"></i>
                  This project still runs on the old owner rules. Assigning a
                  country switches it to role-based access, and a country cannot be
                  changed afterwards.
                </div>
                <div className="mb-2">
                  <ThemedSelect
                    value={countryId}
                    options={creatableCountries.map(c => ({ value: String(c.id), label: c.name }))}
                    onChange={(id) => setCountryId(id)}
                    placeholder="Select country"
                    ariaLabel="Country to assign"
                  />
                </div>
                <label className="ev-reg-p-dark mb-1">
                  Initial Members <span className="ev-small-p-blue"><i>(Optional)</i></span>
                </label>
                {renderMemberPicker()}
                <div className="mt-2 text-end">
                  <button
                    type="button"
                    className="ev-btn-blue-outline-round ev-bold-reg"
                    onClick={handleAssignCountry}
                  >
                    <i className="bi bi-globe"></i> Assign Country
                  </button>
                </div>
              </div>
            ) : (
              <div className="form-group mb-3">
                <label className="ev-reg-p-dark mb-2">
                  Project Members
                  <span className="badge bg-warning ev-btn-small ms-2">
                    {Object.keys(memberRoles).length} selected
                  </span>
                </label>
                {renderMemberPicker()}
                {renderSelfMembershipNotice()}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 text-end">
          {!readOnly && accessDeniedMessage && (
            <div className="ev-alert-card-warning mb-3 text-start">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {accessDeniedMessage} Your changes are kept on this page.
            </div>
          )}
          {!readOnly && (
            <>
              <button
                type="submit"
                className="ev-btn-blue-fill-round ev-bold-reg"
                disabled={isValidatingMembers}
              >
                <i className="bi bi-cursor-fill"></i> {editingFabric ? 'Update Project' : 'Create Project'}
              </button>
              <button
                type="button"
                className="ev-btn-blue-outline-round ms-2"
                onClick={() => runWithRoleChangeGuard(onCancel)}
              >
                Cancel
              </button>
            </>
          )}
        </div>
        </fieldset>
      </form>

      {/* Outside the form on purpose: a disabled fieldset disables the buttons
          inside it too, which would leave a view-only user unable to close. */}
      {readOnly && (
        <div className="mt-4 text-end">
          <button
            type="button"
            className="ev-btn-blue-fill-round ev-bold-reg"
            onClick={onCancel}
          >
            Close
          </button>
        </div>
      )}
      
      {/* Popups */}
      {handoverMember && (
        <div className="modal fade show d-block bg-dark bg-opacity-50">
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <p className="mb-0 ev-fs-15 ev-bold-med">Hand Over Project</p>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => { setHandoverMember(null); setHandoverNewPm(null); }}
                ></button>
              </div>

              <div className="modal-body ev-fs-12">
                <p className="text-danger mb-3">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  <b>{handoverMember.email}</b> is this project's only Project Manager.
                  Choose who takes it over &mdash; they become the new Project Manager and{' '}
                  {handoverMember.email} is removed from the project.
                </p>

                <label className="ev-reg-p-dark mb-1">
                  New Project Manager <span className="text-danger">*</span>
                </label>
                <FormUsersList
                  apiBaseUrl="/pceapi/teams"
                  ownerEmail={handoverMember.email}
                  selectedUsers={handoverNewPm ? [handoverNewPm.user_id] : []}
                  onSelectUser={(userId, user) =>
                    setHandoverNewPm(prev =>
                      prev && prev.user_id === userId
                        ? null
                        : { user_id: userId, email: user.email }
                    )
                  }
                />

                <p className="ev-small-p-dark mt-3 mb-0">
                  <i className="bi bi-info-circle me-1"></i>
                  The project's creator label, its Dossiers and its reports move across
                  as well, along with the Data Folders {handoverMember.email} created
                  inside this project.
                </p>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary ev-btn-reg"
                  onClick={() => { setHandoverMember(null); setHandoverNewPm(null); }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn ev-btn-blue-fill-round ev-btn-reg"
                  disabled={!handoverNewPm}
                  onClick={handleHandoverSubmit}
                >
                  {handoverNewPm
                    ? `Hand over to ${handoverNewPm.email}`
                    : 'Hand Over Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}

      <ConfirmationModal
        show={!!pendingLeaveAction}
        onClose={cancelLeave}
        onConfirm={confirmLeave}
        title="Leave project details?"
        message="Unsaved changes to this project (name, description, tags, folder selection and member changes) will be lost. Your saved project is not affected."
        confirmButtonText="Continue to Data Folder"
        confirmButtonClass="btn-primary"
        cancelButtonText="Stay on this page"
      />

      <ConfirmationModal
        show={!!pendingDiscardAction}
        onClose={cancelDiscard}
        onConfirm={confirmDiscard}
        title="Unsaved member changes"
        message={"You have member changes that haven't been saved. They are only saved "
          + "when you click Update Project. If you leave now, they will be lost."}
        confirmButtonText="Leave without saving"
        confirmButtonClass="btn-primary"
        cancelButtonText="Keep editing"
      />
    </div>
  );
};

export default DataFabricForm;